/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React from 'react'

import { Dropdown, type DropdownOption } from '@/components/ui'

import { useAdminCustomers } from './useAdminCustomers'

interface Props {
  value?: number
  onChange: (customerId?: number) => void
  label?: string
}

/**
 * Single-select over the Timescale `customer` table (useAdminCustomers), for the admin
 * pages. The dashboard's CompanyDropdown lists the Fresh hierarchy instead, which is
 * not what a profile's customer_id points at.
 */
export const CustomerDropdown: React.FC<Props> = ({ value, onChange, label = 'Company' }) => {
  const { customers, isLoading, error } = useAdminCustomers()
  const options: DropdownOption[] = customers.map((c) => ({ label: c.name, value: String(c.customerId) }))
  const selectedItem = options.find((o) => Number(o.value) === value) ?? { label: '', value: '' }

  return (
    <div data-testid="customer-dropdown">
      <label>{label}</label>
      <Dropdown
        options={options}
        selectedItem={selectedItem}
        handleSingleSelectItem={(option: DropdownOption | null) =>
          onChange(option && option.value ? Number(option.value) : undefined)
        }
        placeholder={isLoading ? 'Loading companies...' : 'Select company...'}
      />
      {error && <span className="label-text-alt mt-1 block text-error">{error}</span>}
    </div>
  )
}

export default CustomerDropdown
